import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Stack,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Add,
  Delete,
  Edit,
} from '@mui/icons-material';
import { useConfig } from '../../contexts/ConfigContext';
import { useConfiguredEnvironments } from '../../contexts/EnvironmentsContext';
import { updateConfig } from '../../firebase';
import type { UserGroup, ProgramGroup } from '@finvari/deliver';

interface GroupDialogState {
  open: boolean;
  mode: 'add' | 'edit';
  type: 'user' | 'program';
  id: string;
  name: string;
  description: string;
  items: string; // comma-separated
}

const ConfigEditorPage: React.FC = () => {
  const navigate = useNavigate();
  const { config, userProfile, loading: configLoading } = useConfig();
  const { environments, environmentsById } = useConfiguredEnvironments();

  // Local state for editing
  const [modulesText, setModulesText] = useState('');
  const [protectedEnvironments, setProtectedEnvironments] = useState<string[]>([]);
  const [userGroups, setUserGroups] = useState<Record<string, UserGroup>>({});
  const [programGroups, setProgramGroups] = useState<Record<string, ProgramGroup>>({});

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);

  // Group dialog state
  const [groupDialog, setGroupDialog] = useState<GroupDialogState>({
    open: false,
    mode: 'add',
    type: 'user',
    id: '',
    name: '',
    description: '',
    items: '',
  });

  // Initialize state from config
  useEffect(() => {
    if (config) {
      setModulesText((config.modules || []).join(', '));
      setProtectedEnvironments(config.protectedEnvironments || []);
      setUserGroups(config.userGroups || {});
      setProgramGroups(config.programGroups || {});
    }
  }, [config]);

  const canEdit = userProfile?.canEditConfig ?? false;

  // Parse modules from comma-separated text
  const parseModules = (text: string): string[] => {
    return text.split(',').map(s => s.trim()).filter(s => s.length > 0);
  };

  // Generic save function
  const saveConfig = useCallback(async (updates: {
    modules?: string[];
    protectedEnvironments?: string[];
    userGroups?: Record<string, UserGroup>;
    programGroups?: Record<string, ProgramGroup>;
  }) => {
    setSaving(true);
    setError(null);

    try {
      await updateConfig(updates);
      setLastSaved(new Date());
    } catch (err: any) {
      console.error('Failed to save config:', err);
      setError(err.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  }, []);

  // Save modules on blur
  const handleModulesBlur = () => {
    const modules = parseModules(modulesText);
    const currentModules = config?.modules || [];
    // Only save if changed
    if (JSON.stringify(modules) !== JSON.stringify(currentModules)) {
      saveConfig({ modules });
    }
  };

  // Save protected environments on change
  const handleProtectedEnvironmentsChange = (newValue: string[]) => {
    setProtectedEnvironments(newValue);
    saveConfig({ protectedEnvironments: newValue });
  };

  // Group dialog handlers
  const openAddGroupDialog = (type: 'user' | 'program') => {
    setGroupDialog({
      open: true,
      mode: 'add',
      type,
      id: '',
      name: '',
      description: '',
      items: '',
    });
  };

  const openEditGroupDialog = (type: 'user' | 'program', id: string) => {
    const group = type === 'user' ? userGroups[id] : programGroups[id];
    if (group) {
      setGroupDialog({
        open: true,
        mode: 'edit',
        type,
        id,
        name: group.name,
        description: group.description,
        items: type === 'user'
          ? (group as UserGroup).emails.join(', ')
          : (group as ProgramGroup).programIds.join(', '),
      });
    }
  };

  const closeGroupDialog = () => {
    setGroupDialog(prev => ({ ...prev, open: false }));
  };

  const saveGroupDialog = async () => {
    const { mode, type, id, name, description, items } = groupDialog;
    const trimmedName = name.trim();
    const trimmedDesc = description.trim();
    const itemsArray = items.split(',').map(s => s.trim()).filter(s => s.length > 0);

    if (!trimmedName) {
      setError('Group name is required');
      return;
    }

    // Generate ID for new groups
    const groupId = mode === 'add'
      ? trimmedName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
      : id;

    // Check for duplicate group ID when adding
    if (mode === 'add') {
      const existing = type === 'user' ? userGroups : programGroups;
      if (groupId in existing) {
        setError(`A ${type} group with the ID "${groupId}" already exists. Choose a different name.`);
        return;
      }
    }

    if (type === 'user') {
      const newUserGroups = {
        ...userGroups,
        [groupId]: { name: trimmedName, description: trimmedDesc, emails: itemsArray },
      };
      setUserGroups(newUserGroups);
      closeGroupDialog();
      await saveConfig({ userGroups: newUserGroups });
    } else {
      const newProgramGroups = {
        ...programGroups,
        [groupId]: { name: trimmedName, description: trimmedDesc, programIds: itemsArray },
      };
      setProgramGroups(newProgramGroups);
      closeGroupDialog();
      await saveConfig({ programGroups: newProgramGroups });
    }
  };

  const deleteGroup = async (type: 'user' | 'program', id: string) => {
    if (type === 'user') {
      const { [id]: _, ...rest } = userGroups;
      setUserGroups(rest);
      await saveConfig({ userGroups: rest });
    } else {
      const { [id]: _, ...rest } = programGroups;
      setProgramGroups(rest);
      await saveConfig({ programGroups: rest });
    }
  };

  if (configLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress />
          <Typography variant="body1" color="text.secondary">
            Loading configuration...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (!canEdit) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          You do not have permission to edit the configuration.
        </Alert>
        <Button onClick={() => navigate('/environments')} sx={{ mt: 2 }}>
          Back to Environments
        </Button>
      </Container>
    );
  }

  const environmentIds = environments.map((env) => env.gcpProjectId);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Edit Configuration
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Manage modules, protected environments, and deployment groups
          </Typography>
        </Box>
        {saving && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">Saving...</Typography>
          </Box>
        )}
        {!saving && lastSaved && (
          <Typography variant="body2" color="text.secondary">
            Saved
          </Typography>
        )}
      </Box>

      {/* Status Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Modules Section */}
      <Card sx={{ mb: 3 }}>
        <CardHeader title="Modules" subheader="Deployable module names" />
        <CardContent>
          <TextField
            fullWidth
            label="Modules"
            placeholder="module1, module2, module3"
            value={modulesText}
            onChange={(e) => setModulesText(e.target.value)}
            onBlur={handleModulesBlur}
            helperText="Comma-separated list of module names (saves on blur)"
            multiline
            rows={2}
          />
        </CardContent>
      </Card>

      {/* Protected Environments Section */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title="Protected Environments"
          subheader="Environments that require special permissions to deploy or reset data"
        />
        <CardContent>
          <FormControl fullWidth>
            <InputLabel id="protected-env-label">Protected Environments</InputLabel>
            <Select
              labelId="protected-env-label"
              multiple
              value={protectedEnvironments}
              onChange={(e) => handleProtectedEnvironmentsChange(e.target.value as string[])}
              input={<OutlinedInput label="Protected Environments" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={environmentsById[value]?.name || value} size="small" />
                  ))}
                </Box>
              )}
            >
              {environmentIds.map((envId) => (
                <MenuItem key={envId} value={envId}>
                  {environmentsById[envId]?.name || envId}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      {/* User Groups Section */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title="User Groups"
          subheader="Predefined sets of admin email addresses for quick selection during deployment"
          action={
            <Button variant="outlined" startIcon={<Add />} onClick={() => openAddGroupDialog('user')}>
              Add Group
            </Button>
          }
        />
        <CardContent>
          {Object.entries(userGroups).length > 0 ? (
            <Stack spacing={2}>
              {Object.entries(userGroups)
                .sort(([, a], [, b]) => a.name.localeCompare(b.name))
                .map(([id, group]) => (
                  <Paper key={id} elevation={1} sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {group.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {group.description || 'No description'}
                        </Typography>
                      </Box>
                      <Box>
                        <IconButton size="small" aria-label={`Edit ${group.name}`} onClick={() => openEditGroupDialog('user', id)}>
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" aria-label={`Delete ${group.name}`} onClick={() => deleteGroup('user', id)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {group.emails.length > 0 ? (
                        group.emails.map((email) => (
                          <Chip key={email} label={email} size="small" variant="outlined" />
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No emails configured
                        </Typography>
                      )}
                    </Box>
                  </Paper>
                ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No user groups configured
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Program Groups Section */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title="Program Groups"
          subheader="Predefined sets of program names for quick selection during deployment"
          action={
            <Button variant="outlined" startIcon={<Add />} onClick={() => openAddGroupDialog('program')}>
              Add Group
            </Button>
          }
        />
        <CardContent>
          {Object.entries(programGroups).length > 0 ? (
            <Stack spacing={2}>
              {Object.entries(programGroups)
                .sort(([, a], [, b]) => a.name.localeCompare(b.name))
                .map(([id, group]) => (
                  <Paper key={id} elevation={1} sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {group.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {group.description || 'No description'}
                        </Typography>
                      </Box>
                      <Box>
                        <IconButton size="small" aria-label={`Edit ${group.name}`} onClick={() => openEditGroupDialog('program', id)}>
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" aria-label={`Delete ${group.name}`} onClick={() => deleteGroup('program', id)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {group.programIds.length > 0 ? (
                        group.programIds.map((programId) => (
                          <Chip key={programId} label={programId} size="small" variant="outlined" />
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No programs configured
                        </Typography>
                      )}
                    </Box>
                  </Paper>
                ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No program groups configured
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Group Edit Dialog */}
      <Dialog open={groupDialog.open} onClose={closeGroupDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {groupDialog.mode === 'add' ? 'Add' : 'Edit'}{' '}
          {groupDialog.type === 'user' ? 'User Group' : 'Program Group'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              value={groupDialog.name}
              onChange={(e) => setGroupDialog(prev => ({ ...prev, name: e.target.value }))}
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={groupDialog.description}
              onChange={(e) => setGroupDialog(prev => ({ ...prev, description: e.target.value }))}
              fullWidth
              multiline
              rows={2}
            />
            <TextField
              label={groupDialog.type === 'user' ? 'Email Addresses' : 'Program IDs'}
              value={groupDialog.items}
              onChange={(e) => setGroupDialog(prev => ({ ...prev, items: e.target.value }))}
              fullWidth
              multiline
              rows={3}
              helperText="Comma-separated list"
              placeholder={
                groupDialog.type === 'user'
                  ? 'user1@example.com, user2@example.com'
                  : 'program1, program2, program3'
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeGroupDialog}>Cancel</Button>
          <Button onClick={saveGroupDialog} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : (groupDialog.mode === 'add' ? 'Add' : 'Save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ConfigEditorPage;
